"use client";

import { useMemo, useState } from "react";

import { fmtIntCA } from "@/lib/format";

import type {
  ContestedMotion,
  ContestedView,
} from "@/app/council/_lib/alignment-data";
import {
  INTENT_LABEL,
  INTENT_TINT,
  SORT_LABEL,
  deriveBodies,
  paginate,
  sortContested,
  sourceShort,
  trimCouncillorPrefix,
  voteTotal,
  type SortKey,
} from "@/app/council/_lib/contested-timeline";

type Props = {
  contested: ContestedView;
  /** When set, renders only the first N motions and skips paging/sort
   *  controls — used by the landing teaser. */
  preview?: number;
  /** Page size when not in preview mode. Default 20. */
  pageSize?: number;
};

export function ContestedTimeline({
  contested,
  preview,
  pageSize = 20,
}: Props) {
  const motions = contested.motions;
  const bodies = useMemo(() => deriveBodies(motions), [motions]);
  const [bodyFilter, setBodyFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("date-desc");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const sorted = useMemo(
    () => sortContested(motions, bodyFilter, sortKey),
    [motions, bodyFilter, sortKey],
  );

  const { isPreview, totalPages, safePage, pageStart, pageEnd } = paginate(
    sorted.length,
    page,
    pageSize,
    preview,
  );
  const visible = sorted.slice(pageStart, pageEnd);

  // Reset to page 1 whenever the filtered/sorted set changes shape.
  const resetPage = (next: () => void) => {
    next();
    setPage(1);
  };

  if (motions.length === 0) {
    return (
      <p className="text-sm text-ink-2 leading-snug">
        No contested motions on file yet for the current term.
      </p>
    );
  }

  return (
    <div>
      {!isPreview && (
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap cs-mono text-xs">
            <span className="text-ink-3 mr-1">body</span>
            <button
              type="button"
              onClick={() => resetPage(() => setBodyFilter("all"))}
              aria-pressed={bodyFilter === "all"}
              className={
                "px-2 py-1 border " +
                (bodyFilter === "all"
                  ? "border-ink bg-ink text-bg"
                  : "border-rule text-ink-2 hover:border-ink-3")
              }
            >
              all · {fmtIntCA(motions.length)}
            </button>
            {bodies.map((b) => {
              const count = motions.filter((m) => m.category_name === b).length;
              const active = bodyFilter === b;
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => resetPage(() => setBodyFilter(b))}
                  aria-pressed={active}
                  className={
                    "px-2 py-1 border " +
                    (active
                      ? "border-ink bg-ink text-bg"
                      : "border-rule text-ink-2 hover:border-ink-3")
                  }
                >
                  {b} · {fmtIntCA(count)}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap cs-mono text-xs">
            <label className="flex items-center gap-2">
              <span className="text-ink-3">sort</span>
              <select
                value={sortKey}
                onChange={(e) => resetPage(() => setSortKey(e.target.value as SortKey))}
                className="border border-rule bg-bg px-2 py-1 text-ink-2"
              >
                {Object.entries(SORT_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <span className="text-ink-3">
              showing{" "}
              <span className="text-ink-2">
                {sorted.length === 0
                  ? "0"
                  : `${fmtIntCA(pageStart + 1)}–${fmtIntCA(pageEnd)}`}
              </span>{" "}
              of {fmtIntCA(sorted.length)}
            </span>
          </div>
        </div>
      )}

      <ol className="flex flex-col">
        {visible.map((m) => (
          <MotionRow
            key={m.motion_id}
            m={m}
            open={openId === m.motion_id}
            onToggle={() =>
              setOpenId((cur) => (cur === m.motion_id ? null : m.motion_id))
            }
          />
        ))}
      </ol>

      {!isPreview && totalPages > 1 && (
        <nav
          aria-label="Contested motions pagination"
          className="mt-5 flex items-center justify-between gap-3 cs-mono text-xs"
        >
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage === 1}
            className="px-3 py-1.5 border border-rule text-ink-2 hover:border-ink-3 disabled:opacity-40 disabled:hover:border-rule"
          >
            ← prev
          </button>
          <span className="text-ink-3">
            page <span className="text-ink-2">{fmtIntCA(safePage)}</span> of{" "}
            {fmtIntCA(totalPages)}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            className="px-3 py-1.5 border border-rule text-ink-2 hover:border-ink-3 disabled:opacity-40 disabled:hover:border-rule"
          >
            next →
          </button>
        </nav>
      )}
    </div>
  );
}

function MotionRow({
  m,
  open,
  onToggle,
}: {
  m: ContestedMotion;
  open: boolean;
  onToggle: () => void;
}) {
  const total = voteTotal(m);
  return (
    <li className="cs-rule">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left grid gap-x-4 gap-y-1 py-3 hover:bg-bg-2 focus:outline-none focus:bg-bg-2 transition-colors"
        style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
      >
        <div className="grid gap-x-4 gap-y-1 sm:grid-cols-[100px_minmax(0,1fr)_auto] items-baseline">
          <div className="cs-mono text-xs text-ink-3">
            {m.meeting_date}
          </div>
          <div className="min-w-0">
            <div className="cs-mono text-xs text-ink-3 mb-0.5">
              {m.category_name}
            </div>
            <div className="text-ink leading-snug pr-2">{m.title}</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <span
              className="cs-pill"
              style={{
                color: INTENT_TINT[m.intent],
                borderColor: INTENT_TINT[m.intent],
              }}
            >
              {INTENT_LABEL[m.intent]}
            </span>
            <span
              className={
                "cs-pill " + (m.is_carried ? "text-moss" : "text-brick")
              }
              style={{
                borderColor: m.is_carried ? "var(--moss)" : "var(--brick)",
              }}
            >
              {m.is_carried ? "carried" : "defeated"}
            </span>
          </div>
        </div>
        <div className="sm:pl-[116px]">
          <VoteBar
            forCount={m.vote_summary.for_count}
            againstCount={m.vote_summary.against_count}
            recusedCount={m.vote_summary.recused_count}
            abstainCount={m.vote_summary.abstain_count}
            total={total}
          />
        </div>
      </button>

      {open && (
        <div className="sm:pl-[116px] pb-5 -mt-1">
          {m.description && (
            <p className="text-sm text-ink-2 leading-relaxed italic">
              {m.description}
            </p>
          )}

          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <Roster
              label="for"
              tint="var(--moss)"
              entries={m.voters.for}
            />
            <Roster
              label="against"
              tint="var(--brick)"
              entries={m.voters.against}
            />
            {m.voters.recused.length > 0 && (
              <Roster
                label="recused"
                tint="var(--amber)"
                entries={m.voters.recused}
              />
            )}
            {m.voters.abstain.length > 0 && (
              <Roster
                label="abstain"
                tint="var(--ink-3)"
                entries={m.voters.abstain}
              />
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 cs-mono text-xs text-ink-3">
            {m.bylaw_numbers && m.bylaw_numbers.length > 0 && (
              <span>
                bylaw{m.bylaw_numbers.length === 1 ? "" : "s"}:{" "}
                <span className="text-ink-2">
                  {m.bylaw_numbers.join(", ")}
                </span>
              </span>
            )}
            {m.ward != null && (
              <span>
                ward: <span className="text-ink-2">W{m.ward}</span>
              </span>
            )}
            {m.loc_codes && m.loc_codes.length > 0 && (
              <span>
                LU code{m.loc_codes.length === 1 ? "" : "s"}:{" "}
                <span className="text-ink-2">{m.loc_codes.join(", ")}</span>
              </span>
            )}
          </div>

          <a
            href={m.meeting_minutes_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block cs-source-chip"
            data-verified="yes"
          >
            <span>City of Calgary</span>
            <span aria-hidden className="text-ink-3">·</span>
            <span className="text-ink-3 normal-case">
              official meeting minutes — {m.meeting_date}
            </span>
          </a>
        </div>
      )}
    </li>
  );
}

function VoteBar({
  forCount,
  againstCount,
  recusedCount,
  abstainCount,
  total,
}: {
  forCount: number;
  againstCount: number;
  recusedCount: number;
  abstainCount: number;
  total: number;
}) {
  if (total === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <div
        className="flex h-2 flex-1 border border-rule overflow-hidden"
        aria-label={`vote split: ${forCount} for, ${againstCount} against, ${recusedCount} recused, ${abstainCount} abstain`}
      >
        {forCount > 0 && (
          <span
            style={{
              width: `${(forCount / total) * 100}%`,
              background: "var(--moss)",
            }}
          />
        )}
        {againstCount > 0 && (
          <span
            style={{
              width: `${(againstCount / total) * 100}%`,
              background: "var(--brick)",
            }}
          />
        )}
        {recusedCount > 0 && (
          <span
            style={{
              width: `${(recusedCount / total) * 100}%`,
              background: "var(--amber)",
            }}
          />
        )}
        {abstainCount > 0 && (
          <span
            style={{
              width: `${(abstainCount / total) * 100}%`,
              background: "var(--ink-3)",
            }}
          />
        )}
      </div>
      <span className="cs-mono text-xs text-ink-3 whitespace-nowrap">
        {fmtIntCA(forCount)}–{fmtIntCA(againstCount)}
        {recusedCount > 0 ? ` · ${fmtIntCA(recusedCount)} rec` : ""}
      </span>
    </div>
  );
}

function Roster({
  label,
  tint,
  entries,
}: {
  label: string;
  tint: string;
  entries: { id: number; name: string; source: string }[];
}) {
  if (entries.length === 0) {
    return (
      <div>
        <div className="cs-label" style={{ color: tint }}>
          {label} · 0
        </div>
        <p className="mt-1 text-xs text-ink-3 italic">none recorded</p>
      </div>
    );
  }
  return (
    <div>
      <div className="cs-label" style={{ color: tint }}>
        {label} · {fmtIntCA(entries.length)}
      </div>
      <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {entries.map((e) => (
          <li key={e.id} className="text-ink-2 cs-mono text-xs">
            {trimCouncillorPrefix(e.name)}
            {e.source !== "direct_named" && (
              <span
                className="ml-1 text-ink-3 italic"
                title={
                  e.source === "extrapolated_mover"
                    ? "inferred from mover/seconder roles"
                    : e.source === "extrapolated_attendance"
                      ? "inferred from whole-council attendance carry"
                      : "vote source not recorded in minutes"
                }
              >
                ({sourceShort(e.source)})
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
